import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { fmtDateTime } from "../../lib/time";
import { useClientProject } from "../../contexts/ClientProjectContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import { RotateCcw, Filter } from "lucide-react";
import { SelfieThumb } from "../../components/SelfieThumb";

const ALL = "__all";

function buildQS({ frm, to, projectId }) {
  const params = new URLSearchParams();
  if (frm) params.set("from", frm);
  if (to) params.set("to", to);
  if (projectId && projectId !== ALL) params.set("project_id", projectId);
  const s = params.toString();
  return s ? `?${s}` : "";
}

export default function ClientAttendance() {
  const { projectId } = useClientProject();
  const [filters, setFilters] = useState({ frm: "", to: "" });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = buildQS({ ...filters, projectId });
      const r = await api.get(`/client/shifts${qs}`);
      setRows(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [filters, projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const reset = () => setFilters({ frm: "", to: "" });

  return (
    <div className="space-y-6" data-testid="client-attendance-page">
      {/* Filters */}
      <div className="glass-panel p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="h-4 w-4" />
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
            Filters
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-wider">From</Label>
            <Input
              type="date"
              value={filters.frm}
              onChange={(e) => setFilters((f) => ({ ...f, frm: e.target.value }))}
              className="mt-2 rounded-xl bg-white"
              data-testid="client-attendance-filter-from"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">To</Label>
            <Input
              type="date"
              value={filters.to}
              onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))}
              className="mt-2 rounded-xl bg-white"
              data-testid="client-attendance-filter-to"
            />
          </div>
          <div className="flex items-end">
            <Button
              variant="outline"
              className="rounded-xl w-full"
              onClick={reset}
              data-testid="client-attendance-filter-reset"
            >
              <RotateCcw className="h-4 w-4 mr-1" /> Reset
            </Button>
          </div>
        </div>
        {loading && (
          <div className="mt-2 font-mono text-[10px] uppercase tracking-widest text-neutral-500">
            Loading…
          </div>
        )}
      </div>

      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
        {rows.length} shift{rows.length === 1 ? "" : "s"}
      </div>

      <div className="glass-panel overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-white/50 border-b border-slate-200/70">
            <tr>
              {["Photos", "Promoter", "Store", "Check-in", "Check-out", "Status"].map((h) => (
                <th
                  key={h}
                  className="text-left p-3 font-mono text-[10px] uppercase tracking-widest text-neutral-500 whitespace-nowrap"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="p-6 text-center text-neutral-500">
                  No data
                </td>
              </tr>
            ) : (
              rows.map((s) => (
                <tr
                  key={s.id}
                  className="border-t border-slate-200/70"
                  data-testid={`client-shift-row-${s.id}`}
                >
                  <td className="p-3">
                    <div className="flex gap-1">
                      <SelfieThumb path={s.check_in_photo} size={56} alt="check-in" />
                      <SelfieThumb path={s.check_out_photo} size={56} alt="check-out" />
                    </div>
                  </td>
                  <td className="p-3">{s.promoter_name || "—"}</td>
                  <td className="p-3">{s.store_name || "—"}</td>
                  <td className="p-3 font-mono text-xs whitespace-nowrap">
                    {s.check_in_at ? fmtDateTime(s.check_in_at) : "—"}
                  </td>
                  <td className="p-3 font-mono text-xs whitespace-nowrap">
                    {s.check_out_at ? fmtDateTime(s.check_out_at) : "—"}
                  </td>
                  <td className="p-3">
                    <span
                      className={`inline-block px-2 py-1 text-[10px] uppercase tracking-widest border ${
                        s.status === "active"
                          ? "border-emerald-500 text-emerald-600"
                          : "border-slate-300/70 text-neutral-600"
                      }`}
                    >
                      {s.status}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
